// TableFooter — (items-per-page select + range text) · pager. Generic: the caller
// passes already-translated labels so domain wording stays out of the primitive.

import { useTranslation } from 'react-i18next';
import IconButton from './IconButton';
import SelectDropdown from './SelectDropdown';

export interface TableFooterProps {
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  /** Items-per-page choices. Default [6, 10, 20]. */
  limits?: number[];
  /** Translated "Items per page" label. */
  perPageLabel: string;
  /** Translated range line, e.g. "1–6 of 9 projects". */
  rangeText: string;
}

const TableFooter = ({
  page,
  limit,
  total,
  onPage,
  onLimit,
  limits = [6, 10, 20],
  perPageLabel,
  rangeText,
}: TableFooterProps) => {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className='table-footer'>
      <div className='table-footer__start'>
        <label className='table-footer__limit'>
          {perPageLabel}
          <SelectDropdown
            // 'sm' (2.5rem) is deliberately more compact than the native Select
            // this replaced, whose 'sm' was 3rem. The footer sits at the bottom of
            // a clipping card (or a scrolling modal/drawer body) at every call
            // site, hence portal + flip; the panel takes an explicit width so the
            // tiny trigger doesn't squeeze the number against its check mark.
            size='sm'
            portal
            flip
            width={120}
            value={String(limit)}
            onChange={(next) => onLimit(Number(next))}
            options={limits.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </label>

        <span className='table-footer__range'>{rangeText}</span>
      </div>

      <div className='table-footer__pager'>
        <IconButton
          icon='chevron-left'
          variant='ghost'
          className='table-footer__nav'
          aria-label={t('header_back')}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        />
        <span className='table-footer__page'>
          <b>{page}</b> / {pages}
        </span>
        <IconButton
          icon='chevron-right'
          variant='ghost'
          className='table-footer__nav'
          aria-label={t('header_forward')}
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        />
      </div>
    </div>
  );
};

export default TableFooter;
